// Draait het? (W6 ronde 2, besluit V1; research §3.2). Was het vlak Gezondheid;
// sinds het gezondheidsbord (B7, 29-10) staat dit onder de knop Voorwaarden.
//
// Vier vragen van boven naar beneden: hoe liep elke laan, welke runs vragen
// aandacht, welke run bleef stil op nul, en hoe staan de verbindingen met de
// database. Het oordeel komt van de server (GET /api/beheer/gezondheid): wélke
// runs aandacht vragen, wat een stille nul is, waarom de duur niet te meten
// is. Dit vlak kleurt niets op eigen gezag. De enige koppeling die het zelf
// legt — "staat de laatste run van deze laan ook bij Vraagt aandacht?" — is
// opzoeken in wat de server stuurde, geen eigen regel.
//
// De wachters (pure-SQL-controles uit sof/wachters.py) duren op de echte
// database ~4 s. Daarom vraagt dit vlak ze NOOIT bij het openen, alleen als de
// beheerder op "Controleer nu" klikt: anders wacht elke bezoeker vier seconden
// op iets waar hij misschien niet voor kwam.

import { useState, type ReactNode } from "react";

import {
  Card,
  InfoIcon,
  ReasonBadge,
  TableHead,
  Tooltip,
} from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { ApiError, api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { GezondheidResponse, WachtersResponse } from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { VerversIcoon } from "./Iconen";
import { Stoplicht } from "./Stoplicht";
import { knopLicht } from "./stijl";

type Laan = GezondheidResponse["lanen"][number];

const getal = (n: number) => n.toLocaleString("nl-NL");

/** "9 sep." — of null als de server geen (leesbare) datum stuurde. */
function datum(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 };

const chip = {
  fontSize: 11,
  color: color.soft,
  background: color.lineFaint,
  border: `1px solid ${color.line}`,
  borderRadius: radius.pill,
  padding: "1px 8px",
  whiteSpace: "nowrap" as const,
};

const KOLOMMEN =
  "minmax(90px,1fr) minmax(190px,1.9fr) minmax(140px,1.3fr) 90px 60px 130px";

export function Draait() {
  const gezondheid = useResource<GezondheidResponse>(() => api.gezondheid(), []);

  if (gezondheid.error) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={gezondheid.error} onRetry={gezondheid.reload} />
      </Card>
    );
  }
  if (!gezondheid.data) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="de gezondheid wordt opgehaald" />
      </Card>
    );
  }

  const g = gezondheid.data;
  const inAandacht = new Set(g.aandacht.map((a) => a.runId));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <Blok titel="Per laan">
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {g.lanen.length === 0 ? (
            <div style={{ padding: 18 }}>
              <EmptyReason reason="er is nog geen enkele run geladen" />
            </div>
          ) : (
            // Op een smal scherm schuift de tabel binnen zijn kaart, niet de pagina.
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 700 }}>
                <TableHead
                  cols={KOLOMMEN}
                  labels={["Laan", "Laatste run", "Status", "Meldingen", "Runs", "Fouten / budget op"]}
                />
                {g.lanen.map((l, i) => (
                  <LaanRij
                    key={l.laan}
                    laan={l}
                    eerste={i === 0}
                    vraagtAandacht={l.laatste ? inAandacht.has(l.laatste.runId) : false}
                  />
                ))}
              </div>
            </div>
          )}
        </Card>
        {/* Eén keer, onder de tabel: waarom er geen duurkolom is. */}
        <p style={{ fontSize: 12, color: color.faint, marginTop: 8, lineHeight: 1.5 }}>
          {g.duurReden}
        </p>
      </Blok>

      <Blok titel="Vraagt aandacht">
        {g.aandacht.length === 0 ? (
          <Card style={{ padding: 18 }}>
            <EmptyReason reason="geen enkele run vraagt nu aandacht" />
          </Card>
        ) : (
          <Card style={{ padding: 0 }}>
            {g.aandacht.map((a, i) => (
              <div
                key={a.runId}
                style={{
                  padding: "12px 16px",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                  color: color.body,
                }}
              >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  {/* Review ronde 2: het woord van de server, de code in de
                      tooltip — zelfde afspraak als bij de status. */}
                  <span title={a.laan} style={{ fontWeight: weight.medium, color: color.ink }}>
                    {a.laanLabel}
                  </span>
                  <span style={mono}>{a.runId}</span>
                  <span style={{ color: color.faint }}>{datum(a.geladenOp) ?? "datum onbekend"}</span>
                  {/* Het woord komt van de server; de code staat in de tooltip
                      voor wie hem in de logboeken wil terugzoeken. */}
                  <ReasonBadge text={a.statusLabel} tone="amber" title={a.status} />
                  <span style={{ color: color.faint }}>
                    {getal(a.meldingen)} {a.meldingen === 1 ? "melding" : "meldingen"}
                  </span>
                </div>
                {Object.keys(a.soorten).length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {/* Grootste soort eerst: daar zit het meeste werk. Het
                        woord komt uit `meldingLabels` (review ronde 2); kent
                        de server er geen, dan de code — kaal maar eerlijk. */}
                    {Object.entries(a.soorten)
                      .sort((x, y) => y[1] - x[1])
                      .map(([soort, n]) => (
                        <span key={soort} style={chip} title={soort}>
                          {`${g.meldingLabels[soort] ?? soort} ${getal(n)}`}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}
      </Blok>

      <Blok titel="Stille nul">
        <Card style={{ padding: g.stilleNul.items.length ? 0 : 18 }}>
          {g.stilleNul.items.length === 0 ? (
            <p style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
              {g.stilleNul.toelichting}
            </p>
          ) : (
            g.stilleNul.items.map((s, i) => (
              <div
                key={`${s.runId}-${s.sector}`}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  padding: "10px 16px",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                  color: color.body,
                }}
              >
                <span style={{ fontWeight: weight.medium, color: color.ink }}>{s.sector}</span>
                <span style={mono}>{s.runId}</span>
                <span style={{ color: color.faint }}>{datum(s.geladenOp) ?? "datum onbekend"}</span>
              </div>
            ))
          )}
        </Card>
      </Blok>

      <Blok titel="Verbindingen met de database">
        <Card style={{ padding: "14px 18px" }}>
          {g.pool ? (
            <>
              <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                <KleinGetal n={g.pool.grootte} label="open" />
                <KleinGetal n={g.pool.vrij} label="vrij" />
                <KleinGetal n={g.pool.wachtend} label="wachtend" />
                <KleinGetal n={g.pool.maximum} label="maximum" />
              </div>
              <p style={{ fontSize: 12, color: color.faint, marginTop: 8, lineHeight: 1.5 }}>
                Hoeveel lijnen de dienst nu open heeft naar de database. Wachtend boven nul
                betekent: vragen staan in de rij.
              </p>
            </>
          ) : (
            <EmptyReason reason={g.poolReden ?? "de server gaf geen stand en geen reden"} />
          )}
        </Card>
      </Blok>

      <Wachters totaal={g.wachtersTotaal} />
    </div>
  );
}

function Blok({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section>
      <h2 style={{ ...sectionLabel }}>{titel}</h2>
      {children}
    </section>
  );
}

function KleinGetal({ n, label }: { n: number; label: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 18,
          fontWeight: weight.bold,
          color: color.ink,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {getal(n)}
      </div>
      <div style={{ ...sectionLabel, marginBottom: 0, marginTop: 3 }}>{label}</div>
    </div>
  );
}

function LaanRij({
  laan,
  eerste,
  vraagtAandacht,
}: {
  laan: Laan;
  eerste: boolean;
  vraagtAandacht: boolean;
}) {
  const l = laan.laatste;
  const tellingen = l ? Object.entries(l.tellingen) : [];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: KOLOMMEN,
        alignItems: "center",
        padding: "10px 16px",
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        fontSize: size.meta,
        color: color.body,
      }}
    >
      <span title={laan.laan} style={{ fontWeight: weight.medium, color: color.ink }}>
        {laan.laanLabel}
      </span>

      {l ? (
        <>
          <span style={{ minWidth: 0 }}>
            {/* De tellingen van de laatste run zitten achter de muis: de rij
                blijft rustig, de getallen zijn één beweging weg. */}
            <Tooltip
              maxWidth={240}
              text={
                tellingen.length ? (
                  <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {tellingen.map(([k, v]) => (
                      <span key={k}>{`${k} ${getal(v)}`}</span>
                    ))}
                  </span>
                ) : (
                  "Deze run bewaarde geen tellingen."
                )
              }
            >
              <span style={{ cursor: "help" }}>
                <span style={mono}>{l.runId}</span>
                <span style={{ color: color.faint }}>
                  {" · "}
                  {datum(l.geladenOp) ?? "datum onbekend"}
                </span>
              </span>
            </Tooltip>
          </span>
          <span>
            <ReasonBadge
              text={l.statusLabel}
              tone={vraagtAandacht ? "amber" : "neutral"}
              title={
                vraagtAandacht
                  ? `Deze run staat ook bij Vraagt aandacht (${l.status}).`
                  : l.status
              }
            />
          </span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{getal(l.meldingen)}</span>
        </>
      ) : (
        // Eén reden over drie kolommen, niet drie keer hetzelfde streepje.
        <span style={{ gridColumn: "2 / 5" }}>
          <EmptyReason reason="nog geen run geladen" />
        </span>
      )}

      <span style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
        {getal(laan.totaal)}
      </span>
      <span style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
        {`${getal(laan.metFouten)} / ${getal(laan.budgetOp)}`}
        {/* Neutraal, niet rood (review ronde 2): dit vlak kleurt niets op
            eigen gezag. Welke run aandacht vraagt zegt de server, en die
            staat al amber bij Vraagt aandacht. */}
        {laan.mislukt > 0 && (
          <span style={{ color: color.faint }}>{` · ${getal(laan.mislukt)} mislukt`}</span>
        )}
      </span>
    </div>
  );
}

type WachterStaat =
  | { soort: "rust" }
  | { soort: "bezig" }
  | { soort: "klaar"; data: WachtersResponse }
  | { soort: "fout"; reden: string };

function Wachters({ totaal }: { totaal: number }) {
  const [staat, setStaat] = useState<WachterStaat>({ soort: "rust" });
  const bezig = staat.soort === "bezig";

  async function controleer() {
    setStaat({ soort: "bezig" });
    try {
      setStaat({ soort: "klaar", data: await api.wachters() });
    } catch (err) {
      setStaat({
        soort: "fout",
        reden: err instanceof ApiError ? err.reason : "De controles konden niet draaien.",
      });
    }
  }

  const klaar = staat.soort === "klaar" ? staat.data : null;

  return (
    <section>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <h2 style={{ ...sectionLabel, marginBottom: 0 }}>Wachters</h2>
        <button
          type="button"
          onClick={() => void controleer()}
          disabled={bezig}
          style={{
            ...knopLicht,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: bezig ? color.faint : color.body,
            cursor: bezig ? "default" : "pointer",
          }}
        >
          <VerversIcoon /> {bezig ? "bezig… (± 4 s)" : "Controleer nu"}
        </button>
      </div>

      <Card style={{ padding: klaar && klaar.wachters.length ? 0 : 18 }}>
        {staat.soort === "rust" && (
          <p style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.5 }}>
            {getal(totaal)} {totaal === 1 ? "controle" : "controles"} die elk nul rijen horen te
            vinden. Samen duren ze een paar seconden, daarom draaien ze pas als je op
            Controleer nu klikt.
          </p>
        )}
        {staat.soort === "bezig" && (
          <EmptyReason reason="de controles lopen — dit duurt ongeveer vier seconden" />
        )}
        {staat.soort === "fout" && (
          <p style={{ fontSize: size.meta, color: color.accentRed }}>{staat.reden}</p>
        )}
        {klaar && klaar.wachters.length === 0 && (
          <EmptyReason reason="de server gaf geen enkele controle terug" />
        )}
        {klaar && klaar.wachters.length > 0 && (
          <>
            {klaar.wachters.map((w, i) => (
              <div
                key={w.sleutel}
                data-wachter={w.sleutel}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 16px",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                }}
              >
                {/* "onbekend" = de controle kon niet draaien: grijs, niet groen
                    en niet rood — er is niets gemeten. */}
                <Stoplicht stand={w.stand === "onbekend" ? "leeg" : w.stand} />
                <span style={{ color: color.ink, flex: 1, minWidth: 0 }}>{w.naam}</span>
                <InfoIcon tooltip={w.uitleg} />
                <span
                  style={{
                    minWidth: 90,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    color: w.stand === "rood" ? color.accentRed : color.faint,
                  }}
                >
                  {w.gevonden === null || w.gevonden === undefined ? (
                    <EmptyReason reason={w.reden ?? "de controle kon niet draaien"} />
                  ) : (
                    `${getal(w.gevonden)} gevonden`
                  )}
                </span>
              </div>
            ))}
            <div
              style={{
                padding: "10px 16px",
                borderTop: `1px solid ${color.lineFaint}`,
                fontSize: 12,
                color: color.faint,
                lineHeight: 1.5,
              }}
            >
              <p>
                {`Klaar in ${(klaar.duurMs / 1000).toLocaleString("nl-NL", {
                  maximumFractionDigits: 1,
                })} s.`}
              </p>
              {klaar.alleenBijHetLaden.length > 0 && (
                <p>
                  {`Controleert alleen de loader bij het laden: ${klaar.alleenBijHetLaden.join(", ")}.`}
                </p>
              )}
            </div>
          </>
        )}
      </Card>
    </section>
  );
}
